import { Button, Drawer, Flex, Form, Input, Select, Switch, Typography } from "antd";
import { useEffect } from "react";
import {
  createZaloGroupMessage,
  updateZaloGroupMessage,
} from "../../../api/zaloGroupMessage/zaloGroupMessage";
import { useAction } from "../../../lib/useAsync";
import { neutral, spacing } from "../../../theme/tokens";

const { Text } = Typography;

/** Khớp giới hạn của gateway (ZaloGroupService::MAX_TEXT) — BE cũng validate đúng số này. */
const MAX_TEXT = 1500;

/**
 * Thêm / sửa một cấu hình tin nhóm.
 *
 * Một drawer cho cả hai việc (`record` rỗng = thêm mới) vì hai form dùng đúng một bộ ô; tách
 * làm hai file như các màn cũ chỉ nhân đôi phần khai báo ô mà không thêm gì.
 *
 * Khi SỬA thì `code` bị khóa — cùng với phạm vi, đó là khóa xác định dòng nào áp cho sự kiện
 * nào (unique code+branch_id ở BE).
 *
 * PHẠM VI ÁP DỤNG cố tình KHÔNG có ô nhập: mọi cấu hình tạo ở đây áp cho toàn bộ đơn vị
 * (BE tự lưu branch_id = 0). Backend vẫn hỗ trợ dòng riêng theo đơn vị và service vẫn ưu tiên
 * dòng riêng trước dòng chung — khi nào cần thì mở lại ô chọn ở đây là chạy, không phải sửa BE.
 */
export default function ZaloGroupMessageForm({
  open,
  record,
  events = [],
  usedCodes = [],
  onClose,
  onSaved,
}) {
  const [form] = Form.useForm();
  const isEdit = Boolean(record?.id);

  /*
   * Mỗi sự kiện chỉ được MỘT cấu hình (unique code+branch_id ở BE), nên khi thêm mới phải bỏ
   * các sự kiện đã cấu hình khỏi danh sách chọn — để đó thì người dùng điền hết form rồi mới
   * bị chặn lúc Lưu. Khi SỬA thì giữ nguyên cả danh sách, nếu không Select mất label của
   * chính dòng đang sửa (code của nó đã nằm trong usedCodes).
   */
  const eventOptions = (isEdit ? events : events.filter((e) => !usedCodes.includes(e.code))).map(
    (e) => ({ value: e.code, label: `${e.name} (${e.code})` })
  );

  const save = useAction(isEdit ? updateZaloGroupMessage : createZaloGroupMessage, {
    form,
    successMessage: isEdit ? "Đã cập nhật cấu hình tin" : "Đã thêm cấu hình tin",
    onSuccess: () => {
      onSaved?.();
      onClose();
    },
  });

  useEffect(() => {
    if (!open) return;

    if (isEdit) {
      form.setFieldsValue({
        code: record.code,
        name: record.name,
        content: record.content,
        group_id: record.group_id,
        brandname: record.brandname,
        is_active: record.is_active,
      });
      return;
    }

    form.resetFields();
    form.setFieldsValue({ is_active: true });
  }, [open, record, isEdit, form]);

  /** Chọn sự kiện thì điền luôn tên hiển thị mặc định, đỡ phải tự nghĩ. */
  const handleEventChange = (code) => {
    const event = events.find((e) => e.code === code);
    if (event && !form.getFieldValue("name")) {
      form.setFieldsValue({ name: event.name });
    }
  };

  const onFinish = (values) =>
    save.run(isEdit ? { id: record.id, ...values } : values);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="min(560px, 100vw)"
      destroyOnClose
      title={isEdit ? record?.name || "Sửa cấu hình tin nhóm" : "Thêm cấu hình tin nhóm"}
      footer={
        <Flex justify="end" gap={spacing[2]}>
          <Button onClick={onClose}>Hủy</Button>
          <Button type="primary" loading={save.loading} onClick={() => form.submit()}>
            Lưu
          </Button>
        </Flex>
      }
    >
      <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          label="Sự kiện"
          name="code"
          rules={[{ required: true, message: "Vui lòng chọn sự kiện" }]}
          extra={
            isEdit
              ? "Không đổi được sự kiện của cấu hình đã tạo."
              : "Mỗi sự kiện chỉ một cấu hình — sự kiện đã cấu hình không còn trong danh sách."
          }
        >
          <Select
            disabled={isEdit}
            placeholder="Chọn sự kiện phát sinh tin"
            onChange={handleEventChange}
            options={eventOptions}
            notFoundContent="Mọi sự kiện đều đã có cấu hình"
          />
        </Form.Item>

        <Form.Item
          label="Tên hiển thị"
          name="name"
          rules={[{ required: true, message: "Tên hiển thị không được để trống" }]}
        >
          <Input placeholder="Ví dụ: Khách gửi phản ánh mới" />
        </Form.Item>

        <Form.Item
          label="Mã nhóm Zalo"
          name="group_id"
          rules={[{ required: true, message: "Mã nhóm Zalo không được để trống" }]}
          extra="Group ID của nhóm nhận tin. Mỗi sự kiện gửi được vào một nhóm khác nhau."
        >
          <Input placeholder="Ví dụ: b8887e045768be36e779" />
        </Form.Item>

        <Form.Item
          label="Brandname"
          name="brandname"
          rules={[{ required: true, message: "Brandname không được để trống" }]}
          extra="Brandname đăng ký với gateway để gửi nhóm — không liên quan OA ZNS của đơn vị."
        >
          <Input placeholder="Ví dụ: SHINKO CORP" />
        </Form.Item>

        <Form.Item
          label="Nội dung tin gửi vào nhóm"
          name="content"
          rules={[
            { required: true, message: "Nội dung tin không được để trống" },
            { max: MAX_TEXT, message: `Nội dung không vượt quá ${MAX_TEXT} ký tự` },
          ]}
          extra="Xuống dòng được. Tin gửi vào nhóm nên ngắn, nói rõ việc cần làm."
        >
          <Input.TextArea rows={5} showCount maxLength={MAX_TEXT} />
        </Form.Item>

        <Form.Item
          label="Bật gửi tin"
          name="is_active"
          valuePropName="checked"
          extra="Tắt thì không gửi loại tin này, nội dung đã soạn vẫn giữ. Các loại tin khác vẫn chạy."
        >
          <Switch />
        </Form.Item>

        {isEdit && (
          <Text style={{ color: neutral[500] }}>
            Mã sự kiện: <Text code>{record?.code}</Text>
          </Text>
        )}
      </Form>
    </Drawer>
  );
}
